import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MajofestForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState('');
  const [isLoadingAres, setIsLoadingAres] = useState(false);
  const [ico, setIco] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    vendorName: '',
    vatPayer: '',
    invoiceDetails: '',
    website: '',
    description: '',
    menu: '',
    stallSize: '',
    electricConnection: '',
    notes: '',
    photos: [],
    eventDays: 'jednodenni',  // Májofest je jednodenní
    vendorType: ''  // grill-foodtrucky nebo doplnkovy-sortiment
  });

  // Kalkulace celkové částky - MÁJOFEST (bez vratné kauce)
  const calculateTotal = () => {
    if (!formData.vendorType || !formData.electricConnection) {
      return null;
    }

    // Cena za nájem podle typu - jednodenní akce
    let rentTotal = 0;
    if (formData.vendorType === 'grill-foodtrucky') {
      rentTotal = 5000;
    } else if (formData.vendorType === 'doplnkovy-sortiment') {
      rentTotal = 3000;
    }

    // Cena za energii
    let energyCost = 0;
    if (formData.electricConnection === 'DO 3KW') {
      energyCost = 1750;
    } else if (formData.electricConnection === '3KW až 6KW') {
      energyCost = 3300;
    } else if (formData.electricConnection === 'nad 6KW') {
      energyCost = 4500;
    }

    // BEZ vratné kauce pro Májofest
    return {
      rent: rentTotal,
      energy: energyCost,
      total: rentTotal + energyCost
    };
  };

  const priceCalc = calculateTotal();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const loadFromAres = async () => {
    if (!ico || ico.length < 8) {
      toast({
        title: "Chyba",
        description: "Zadejte platné IČO (8 číslic)",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingAres(true);

    try {
      const response = await axios.get(`${BACKEND_URL}/api/ares/company/${ico}`);
      
      if (response.data.success) {
        // Automaticky vyplň formulář daty z ARES
        setFormData(prev => ({
          ...prev,
          vatPayer: response.data.vatPayer,
          invoiceDetails: response.data.invoiceDetails
        }));

        toast({
          title: "Údaje načteny!",
          description: `Firma ${response.data.nazev} byla nalezena v registru ARES`,
        });
      }
    } catch (error) {
      console.error('ARES error:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.detail || "Nepodařilo se načíst údaje z ARES",
        variant: "destructive",
      });
    } finally {
      setIsLoadingAres(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 5) {
      toast({
        title: "Příliš mnoho fotek",
        description: "Maximálně můžete nahrát 5 fotografií",
        variant: "destructive",
      });
      return;
    }

    // Vytvoř preview
    const previews = files.map(file => URL.createObjectURL(file));
    setPhotosPreviews(previews);
    setPhotos(files);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newPreviews = photosPreviews.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    setPhotosPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Nejdřív nahraj fotky, pokud jsou
      let photoUrls = [];
      if (photos.length > 0) {
        setIsUploadingPhotos(true);
        const formDataPhotos = new FormData();
        photos.forEach(photo => {
          formDataPhotos.append('files', photo);
        });

        const uploadResponse = await axios.post(`${BACKEND_URL}/api/upload/vendor-photos`, formDataPhotos, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (uploadResponse.data.success) {
          photoUrls = uploadResponse.data.files;
          toast({
            title: "Fotky nahrány!",
            description: `Nahráno ${photoUrls.length} fotografií`,
          });
        }
        setIsUploadingPhotos(false);
      }

      // Přidej URL fotek, totalPrice a eventId do formData
      const priceCalculation = calculateTotal();
      const submissionData = {
        ...formData,
        photos: photoUrls,
        totalPrice: priceCalculation ? priceCalculation.total : null,
        eventId: 'majofest-otrokovice-2026'  // ID Májofest Otrokovice
      };

      // Real API call
      const response = await axios.post(`${BACKEND_URL}/api/vendor-applications`, submissionData);
      
      if (response.data) {
        setIsSubmitted(true);
        setRegistrationId(response.data.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast({
          title: "Přihláška odeslána!",
          description: "Brzy vás budeme kontaktovat s dalšími informacemi.",
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.detail || "Nepodařilo se odeslat přihlášku. Zkuste to prosím znovu.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploadingPhotos(false);
    }
  };

  if (isSubmitted) {
    const statusUrl = `${window.location.origin}/status/${registrationId}`;
    
    return (
      <Card className="border-2 border-orange-300 bg-orange-50">
        <CardContent className="p-12 text-center">
          <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-slate-900 mb-4">Děkujeme za přihlášku!</h3>
          <p className="text-lg text-slate-700 mb-6">
            Vaše registrace byla úspěšně odeslána. Brzy vás budeme kontaktovat s dalšími informacemi.
          </p>
          
          <div className="bg-white border-2 border-orange-200 rounded-lg p-6 mb-6 max-w-2xl mx-auto">
            <p className="text-sm text-slate-600 mb-2">📋 Registrační číslo:</p>
            <p className="text-2xl font-bold text-orange-500 mb-4">{registrationId}</p>
            
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-blue-900 mb-3">
                🔗 Zkontrolujte stav Vaší přihlášky:
              </p>
              <a 
                href={statusUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all text-sm font-mono block mb-3"
              >
                {statusUrl}
              </a>
              <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                💡 Tento link si uložte! Můžete se k němu kdykoliv vrátit a zkontrolovat, 
                zda byla Vaše přihláška schválena. Po schválení zde najdete také důležité 
                informace o platbě a termínech.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 max-w-2xl mx-auto text-left">
            <p className="text-sm text-amber-900">
              <strong>ℹ️ Co bude dál:</strong><br/>
              1. Vaše přihláška čeká na schválení organizátorem<br/>
              2. O výsledku se dozvíte prostřednictvím odkazu výše<br/>
              3. Po schválení Vám přijde zálohová faktura<br/>
              4. Po úhradě faktury s Vámi závazně počítáme jako s účastníkem
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-4 border-yellow-500 shadow-2xl bg-gradient-to-br from-gray-900 to-black">
      <CardHeader className="bg-gradient-to-r from-amber-500 to-teal-600">
        <CardTitle className="text-3xl text-white font-black">Registrační formulář - Májofest Otrokovice</CardTitle>
        <CardDescription className="text-white/90 text-lg font-bold">
          Vyplňte prosím všechny povinné údaje. Odesláním formuláře se závazně přihlašujete na Májofest Otrokovice 2026.
        </CardDescription>
      </CardHeader>
      <CardContent className="bg-white">
        {/* Informační box s cenami - MÁJOFEST (bez vratné kauce) */}
        <div className="mb-8 p-6 bg-gradient-to-br from-amber-50 to-teal-50 border-4 border-amber-400 rounded-lg">
          <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
            🍺💰 Ceník a podmínky
          </h3>
          <div className="space-y-3 text-slate-700">
            <div className="flex justify-between items-center p-4 bg-white rounded-lg border-2 border-teal-400">
              <span className="font-bold">Speciality z grilu / Food trucky - max 6x3m:</span>
              <span className="text-teal-600 font-black text-xl">5.000 Kč</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-white rounded-lg border-2 border-amber-400">
              <span className="font-bold">Doplňkový sortiment (sladké, hračky, pochutiny k pivu) - max 3x3m:</span>
              <span className="text-amber-600 font-black text-xl">3.000 Kč</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-white rounded-lg border-2 border-gray-400">
              <span className="font-bold">Energie (celá akce, MAX 12 KW):</span>
              <span className="text-gray-900 font-black text-xl">1.750 - 4.500 Kč</span>
            </div>
            <p className="text-sm text-slate-600 italic mt-2 font-bold">* Ceny jsou uvedeny bez DPH</p>
          </div>
          
          <div className="mt-4 p-4 bg-green-50 border-2 border-green-400 rounded-lg">
            <p className="text-sm text-green-900 font-bold">
              ✓ Bez vratné kauce
            </p>
          </div>
          
          <div className="mt-4 p-4 bg-gradient-to-r from-amber-100 to-teal-100 border-2 border-teal-500 rounded-lg">
            <p className="text-sm text-gray-900 font-bold">
              <strong>🍺 Prodejní doba:</strong><br/>
              • Pátek 2. 5. 2026: 15:00 – 23:30<br/>
              • Jednodenní akce | Pivní festival s regionálními kapelami 🎸
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Základní informace */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900 pb-2 border-b-2 border-orange-200">
              Základní informace
            </h3>

            {/* Typ prodejce - POVINNÉ */}
            <div className="space-y-3 p-4 bg-teal-50 border-2 border-teal-300 rounded-lg">
              <Label className="text-lg font-semibold text-slate-900">
                Typ prodejce *
              </Label>
              <RadioGroup
                name="vendorType"
                value={formData.vendorType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, vendorType: value }))}
                required
                className="relative z-10"
              >
                <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-teal-500 hover:border-teal-600 transition-colors">
                  <RadioGroupItem value="grill-foodtrucky" id="type-grill" className="relative z-10 mt-1" />
                  <div>
                    <Label htmlFor="type-grill" className="font-bold text-lg cursor-pointer">
                      Speciality z grilu / Food trucky 🍖
                    </Label>
                    <p className="text-sm text-slate-600 mt-1 font-semibold">
                      5.000 Kč | Stánek max 6x3m | Grilované speciality, pochutiny k pivu
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-amber-500 hover:border-amber-600 transition-colors">
                  <RadioGroupItem value="doplnkovy-sortiment" id="type-additional" className="relative z-10 mt-1" />
                  <div>
                    <Label htmlFor="type-additional" className="font-bold text-lg cursor-pointer">
                      Doplňkový sortiment 🎪
                    </Label>
                    <p className="text-sm text-slate-600 mt-1 font-semibold">
                      3.000 Kč | Stánek max 3x3m | Sladké, hračky, pochutiny k pivu
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="vas@email.cz"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Jméno a příjmení *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jan Novák"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+420 XXX XXX XXX"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="border-slate-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendorName">Název prodejce *</Label>
              <Input
                id="vendorName"
                name="vendorName"
                type="text"
                placeholder="Např. Food Truck XYZ"
                value={formData.vendorName}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>
          </div>

          {/* Fakturační údaje */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900 pb-2 border-b-2 border-orange-200">
              Fakturační údaje
            </h3>

            {/* IČO s tlačítkem načíst z ARES */}
            <div className="space-y-2">
              <Label htmlFor="ico">IČO *</Label>
              <div className="flex gap-2">
                <Input
                  id="ico"
                  name="ico"
                  type="text"
                  placeholder="12345678"
                  value={ico}
                  onChange={(e) => setIco(e.target.value)}
                  className="border-slate-300"
                  maxLength={8}
                />
                <Button
                  type="button"
                  onClick={loadFromAres}
                  disabled={isLoadingAres || !ico}
                  className="bg-blue-500 hover:bg-blue-600 text-white whitespace-nowrap"
                >
                  {isLoadingAres ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Načítám...
                    </>
                  ) : (
                    'Načíst z ARES'
                  )}
                </Button>
              </div>
              <p className="text-sm text-slate-500">Zadejte IČO a klikněte na "Načíst z ARES" pro automatické vyplnění</p>
            </div>

            <div className="space-y-3">
              <Label>Plátce DPH *</Label>
              <RadioGroup
                name="vatPayer"
                value={formData.vatPayer}
                onValueChange={(value) => setFormData(prev => ({ ...prev, vatPayer: value }))}
                required
                className="relative z-10"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ano" id="vat-yes" className="relative z-10" />
                  <Label htmlFor="vat-yes" className="font-normal cursor-pointer">Ano (vyplňte DIČ v fakturačních údajích)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ne" id="vat-no" className="relative z-10" />
                  <Label htmlFor="vat-no" className="font-normal cursor-pointer">Ne</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoiceDetails">
                Fakturační údaje (název subjektu, IČ, DIČ, fakturační adresa) *
              </Label>
              <Textarea
                id="invoiceDetails"
                name="invoiceDetails"
                placeholder="Např:&#10;Název: Food Truck s.r.o.&#10;IČ: 12345678&#10;DIČ: CZ12345678&#10;Adresa: Hlavní 123, 123 45 Praha"
                value={formData.invoiceDetails}
                onChange={handleInputChange}
                required
                rows={5}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Vyplňte kompletní údaje dle registru</p>
            </div>
          </div>

          {/* Kalkulace celkové částky - MÁJOFEST */}
          {priceCalc && (
            <div className="p-6 bg-gradient-to-br from-amber-50 to-teal-50 border-4 border-teal-500 rounded-lg">
              <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
                🍺💰 Kalkulace celkové částky
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border-2 border-teal-400">
                  <span className="text-slate-700 font-bold">
                    Nájem (jednodenní akce - 2.5.2026):
                  </span>
                  <span className="font-black text-xl text-teal-600">{priceCalc.rent.toLocaleString()} Kč</span>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border-2 border-gray-400">
                  <span className="text-slate-700 font-bold">Energie (celá akce):</span>
                  <span className="font-black text-xl">{priceCalc.energy.toLocaleString()} Kč</span>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-amber-500 to-teal-600 rounded-lg text-white">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-black">CELKEM K ÚHRADĚ:</span>
                  <span className="text-3xl font-black">{priceCalc.total.toLocaleString()} Kč bez DPH</span>
                </div>
                <p className="text-sm mt-2 font-bold">* Všechny uvedené ceny jsou bez DPH</p>
                <p className="text-sm mt-1 font-bold">✓ Bez vratné kauce</p>
              </div>
            </div>
          )}

          {/* Informace o prodejci */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900 pb-2 border-b-2 border-orange-200">
              Informace o prodejci
            </h3>

            <div className="space-y-2">
              <Label htmlFor="website">WWW / FB stránky prodejce *</Label>
              <Input
                id="website"
                name="website"
                type="text"
                placeholder="https://www.vase-stranky.cz nebo facebook.com/vasestranky"
                value={formData.website}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Napište o sobě pár vět *
              </Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Krátký popis vašeho konceptu, který použijeme k propagaci na sociálních sítích..."
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Bude použito k propagaci na sociálních sítích</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="menu">
                Předpokládané menu *
              </Label>
              <Textarea
                id="menu"
                name="menu"
                placeholder="Např:&#10;- Burger s hovězím masem&#10;- Vegetariánský burger&#10;- Hranolky&#10;- Domácí limonády"
                value={formData.menu}
                onChange={handleInputChange}
                required
                rows={5}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">Ceny nemusíte uvádět. Bude použito k propagaci.</p>
            </div>
          </div>

          {/* Technické informace */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900 pb-2 border-b-2 border-orange-200">
              Technické údaje
            </h3>

            <div className="space-y-2">
              <Label htmlFor="stallSize">Rozměr stánku *</Label>
              <Input
                id="stallSize"
                name="stallSize"
                type="text"
                placeholder="Např. 3x3m nebo 6x3m"
                value={formData.stallSize}
                onChange={handleInputChange}
                required
                className="border-slate-300"
              />
            </div>

            <div className="space-y-3">
              <Label>Jakou elektrickou přípojku potřebujete? *</Label>
              <RadioGroup
                name="electricConnection"
                value={formData.electricConnection}
                onValueChange={(value) => setFormData(prev => ({ ...prev, electricConnection: value }))}
                required
                className="relative z-10"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="DO 3KW" id="elec-1" className="relative z-10" />
                  <Label htmlFor="elec-1" className="font-normal cursor-pointer">DO 3KW (1.750 Kč)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3KW až 6KW" id="elec-2" className="relative z-10" />
                  <Label htmlFor="elec-2" className="font-normal cursor-pointer">3KW až 6KW (3.300 Kč)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="nad 6KW" id="elec-3" className="relative z-10" />
                  <Label htmlFor="elec-3" className="font-normal cursor-pointer">nad 6KW - max 12 KW (4.500 Kč)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="jine" id="elec-4" className="relative z-10" />
                  <Label htmlFor="elec-4" className="font-normal cursor-pointer">Jiné (upřesním v poznámce)</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Dodatečné informace */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900 pb-2 border-b-2 border-orange-200">
              Dodatečné informace
            </h3>

            {/* Fotografie upload */}
            <div className="space-y-3">
              <Label htmlFor="photos">Reprezentativní fotografie (max 5 ks)</Label>
              <Input
                id="photos"
                name="photos"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="border-slate-300"
              />
              <p className="text-sm text-slate-500">
                Nahrajte až 5 fotografií vašeho stánku, produktů nebo týmu (max 10MB/foto)
              </p>

              {/* Preview fotek */}
              {photosPreviews.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                  {photosPreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Poznámky</Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Máte-li jakékoli dotazy nebo speciální požadavky, napište nám je zde..."
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="border-slate-300"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t-2 border-slate-200">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-900">
              <strong>Upozornění:</strong> Odesláním formuláře se závazně přihlašujete na Májofest Otrokovice 2026. 
                Vaše přihláška bude potvrzena pořadatelem.
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || isUploadingPhotos}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6 font-bold rounded-full"
            >
              {isUploadingPhotos ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Nahrávám fotografie...
                </>
              ) : isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Odesílám přihlášku...
                </>
              ) : (
                'Odeslat přihlášku'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default MajofestForm;
